<template>
  <Page :page-name="'Application Settings'">
    <div class="mt-4">
      <Splitter class="min-h-[85vh] bg-surface-50 dark:bg-surface-800">
        <SplitterPanel class="flex align-items-center justify-content-center" :size="20" :min-size="10">
          <div class="flex flex-col gap-2 mt-4 w-full">
            <PanelMenu :model="items" class="font-bold" pt:headerIcon:class="text-2xl mt-2 text-primary-500" pt:headerlabel:class="mt-2" />
          </div>
        </SplitterPanel>
        <SplitterPanel class="flex align-items-center justify-content-center" :size="80" :min-size="80">
          <div class="w-full p-4 align-middle">
            <SystemConfigPanel v-if="selectedSetting === 'System'" />
            <ReportConfigurationPanel v-else-if="selectedSetting === 'Reports'" />
            <FlexRowTemplateManager v-else-if="selectedSetting === 'Flex'" />
            <CustomizableColumnManager
              v-else-if="selectedSetting === 'BudgetColumns'"
              :initial-rows="null"
              :scope="SettingScope.BUDGET_COLUMN_TEMPLATES"
            />
            <FlexRowDateConfigPanel v-else-if="selectedSetting === 'FlexRowDate'"></FlexRowDateConfigPanel>
            <ProjectStatusConfigPanel v-else-if="selectedSetting === 'ProjectStatus'"></ProjectStatusConfigPanel>
            <CustomBudgetItemManagerPanel v-else-if="selectedSetting === 'CustomBudgetItem'"></CustomBudgetItemManagerPanel>
            <ErrorLogDisplay v-else-if="selectedSetting === 'ErrorLog'" :recent-error-log></ErrorLogDisplay>
            <LicenseKeyUploadPanel v-else-if="selectedSetting === 'LicenseKeyUpload'"></LicenseKeyUploadPanel>
          </div>
        </SplitterPanel>
      </Splitter>
    </div>
  </Page>
</template>

<script setup lang="ts">
import { SettingScope } from "@/enums/SystemSettings";
import { getSystemSettings } from "@/services/SettingsService";
import { CustomFieldListSchema, type CustomField } from "@/types/CustomField";
import type { ApiError } from "@/types/Response";
import type { Setting } from "@/types/Settings";

const selectedSetting = ref<
  "System" | "Reports" | "Flex" | "BudgetColumns" | "FlexRowDate" | "ProjectStatus" | "CustomBudgetItem" | "ErrorLog" | "LicenseKeyUpload" | ""
>("System");
const customFields = ref<CustomField[]>([]);
const systemSettings = ref<Setting[]>([]);
const recentErrorLog = ref<String>("");

onMounted(async (): Promise<void> => {
  getSystemSettings().then((result: Setting[]) => (systemSettings.value = result));

  axios.get("/customfields").then((response) => {
    handleApiResponse(
      response,
      (result: any) => {
        parseData(CustomFieldListSchema, result, customFields);
      },
      (errors: ApiError[]) => {
        error("Could not retrieve custom fields", errors[0].message);
      }
    );
  });

  axios.get("/errorlog/latest").then((response) => {
    handleApiResponse(
      response,
      (result: any) => {
        recentErrorLog.value = result;
      },
      (errors: ApiError[]) => {
        error("Could not retrieve latest API error log", errors[0].message);
      }
    );
  });
});

const items = [
  {
    label: "System",
    icon: "pi pi-server",
    command: () => {
      selectedSetting.value = "System";
    },
  },
  {
    label: "Reports",
    icon: "pi pi-file",
    command: () => {
      selectedSetting.value = "Reports";
    },
  },
  {
    label: "Flex Row Templates",
    icon: "pi pi-arrows-h",
    command: () => {
      selectedSetting.value = "Flex";
    },
  },
  {
    label: "Budget Column Templates",
    icon: "pi pi-arrows-v",
    command: () => {
      selectedSetting.value = "BudgetColumns";
    },
  },
  {
    label: "Flex Row Dates",
    icon: "pi pi-calendar",
    command: () => {
      selectedSetting.value = "FlexRowDate";
    },
  },
  {
    label: "Project Statuses",
    icon: "pi pi-wave-pulse",
    command: () => {
      selectedSetting.value = "ProjectStatus";
    },
  },
  {
    label: "Customizable Budget Items",
    icon: "pi pi-cog",
    command: () => {
      selectedSetting.value = "CustomBudgetItem";
    },
  },
  {
    label: "API Error Log",
    icon: "pi pi-exclamation-triangle",
    command: () => {
      selectedSetting.value = "ErrorLog";
    },
  },
  {
    label: "License Key",
    icon: "pi pi-key",
    command: () => {
      selectedSetting.value = "LicenseKeyUpload";
    },
  },
];
</script>
